import LayoutHR from "layouts/layout-hr";
import React, { useState, useMemo } from "react";
import { Dropdown } from "react-bootstrap";
import { useCanvasHandler } from "hooks/useCanvasHandler";
import Swal from "sweetalert2";
import {
  ApprovalLinesProvider,
  useApprovalLines,
} from "./context/ApprovalLinesContext";
import { getDevelopmentBanner } from "utils/devNotice";
import MasterDataFormV2 from "../MasterDataFormV2";
import { useNavigate } from "react-router-dom";
import { deleteRequest } from "services/api";
import { useUser } from "contexts/v1/masterData/UserContext";

function ApprovalLinesContent() {
  const { approvalLines, filterOptions, handleSearch, refetch, Pagination } =
    useApprovalLines();
  const { users } = useUser();
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const { handleError, handleSuccessToast } = useCanvasHandler();

  const handleCreate = () => {
    setShowModal(true);
    setEditingItem(null);
  };

  const handleEdit = (item) => {
    setShowModal(true);
    setEditingItem(item);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await deleteRequest(`/ahr/approval-lines/${id}`);
        if (response.success) {
          handleSuccessToast(true, "Approval line deleted successfully");
          refetch();
        } else {
          handleError("Failed to delete approval line");
        }
      } catch (error) {
        handleError(error);
      }
    }
  };

  const handleFormSuccess = () => {
    setShowModal(false);
    setEditingItem(null);
    refetch();
    handleSuccessToast(true, "Approval line saved successfully");
  };

  const handleFormError = (error) => {
    handleError(error);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  // Create options for User and Approver dropdowns
  const userOptions = useMemo(() => {
    return (
      users.data?.map((user) => ({
        value: user.id,
        label: `${user.name} (${user.email})`,
      })) || []
    );
  }, [users.data]);

  const formFields = useMemo(
    () => [
      {
        name: "user_id",
        label: "User",
        type: "select",
        required: true,
        options: userOptions,
        placeholder: "Select user",
      },
      {
        name: "approver_id",
        label: "Approver",
        type: "select",
        required: true,
        options: userOptions,
        placeholder: "Select approver",
      },
      {
        name: "level",
        label: "Level",
        type: "number",
        required: true,
        placeholder: "Enter level (e.g., 1)",
        min: 1,
      },
      {
        name: "type",
        label: "Type",
        type: "select",
        required: true,
        options: [
          { value: "time_off", label: "Time Off" },
          { value: "overtime", label: "Overtime" },
          { value: "general", label: "General" },
          // Add other types as needed
        ],
        placeholder: "Select approval type",
      },
    ],
    [userOptions]
  );

  return (
    <LayoutHR>
      {getDevelopmentBanner()}
      <div className="container-xl">
        <div className="page-header d-print-none">
          <div className="row align-items-center">
            <div className="col-auto">
              <button
                className="btn btn-outline-secondary"
                onClick={() => navigate(-1)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="icon icon-tabler icon-tabler-arrow-left"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <line x1="5" y1="12" x2="11" y2="18" />
                  <line x1="5" y1="12" x2="11" y2="6" />
                </svg>
                Back
              </button>
            </div>
            <div className="col">
              <h2 className="page-title">Approval Lines Management</h2>
              <div className="text-muted mt-1">
                Manage approval hierarchies and rules
              </div>
            </div>
            <div className="col-auto">
              <button className="btn btn-primary" onClick={handleCreate}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="icon icon-tabler icon-tabler-plus"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add Approval Line
              </button>
            </div>
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <div className="input-icon">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search..."
                    value={filterOptions.search}
                    onChange={() => {}}
                    onKeyDown={handleSearch}
                  />
                  <span className="input-icon-addon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="icon icon-tabler icon-tabler-search"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      strokeWidth="2"
                      stroke="currentColor"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                      <circle cx="10" cy="10" r="7" />
                      <line x1="21" y1="21" x2="15" y2="15" />
                    </svg>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-vcenter table-hover">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Action</th>
                    <th>User</th>
                    <th>Approver</th>
                    <th>Level</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {approvalLines.loading ? (
                    <tr>
                      <td colSpan="6" className="text-center">
                        <div
                          className="spinner-border spinner-border-sm"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        Loading approval lines...
                      </td>
                    </tr>
                  ) : approvalLines.data?.length > 0 ? (
                    approvalLines.data.map((item, index) => (
                      <tr key={item.id}>
                        <td>
                          {(filterOptions.currentPage - 1) * 10 + index + 1}
                        </td>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle
                              variant="default"
                              size="sm"
                              id={`dropdown-${item.id}`}
                            >
                              Actions
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item onClick={() => handleEdit(item)}>
                                Edit
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={() => handleDelete(item.id)}
                                className="text-danger"
                              >
                                Delete
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                        <td>
                          {item.user?.name} ({item.user?.email})
                        </td>
                        <td>
                          {item.approver?.name} ({item.approver?.email})
                        </td>
                        <td>{item.level}</td>
                        <td>
                          <span className="badge bg-blue-lt capitalize">
                            {item.type?.replace("_", " ")}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center">
                        No approval lines found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {approvalLines.pagination && <Pagination />}
          </div>
        </div>

        {showModal && (
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {editingItem
                      ? "Edit Approval Line"
                      : "Create Approval Line"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseModal}
                  ></button>
                </div>
                <div className="modal-body">
                  <MasterDataFormV2
                    apiUrl="/ahr/approval-lines"
                    initialData={editingItem}
                    fields={formFields}
                    onSuccess={handleFormSuccess}
                    onError={handleFormError}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </LayoutHR>
  );
}

export default function ApprovalLinesPage() {
  return (
    <ApprovalLinesProvider>
      <ApprovalLinesContent />
    </ApprovalLinesProvider>
  );
}
